import React, { useState, useRef } from 'react'
import { Camera, CheckCircle, Upload, Shield, AlertCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'

interface KYCVerificationFlowProps {
  onComplete: (data: {
    documentType: 'drivers_license' | 'passport' | 'state_id'
    documentFront: File
    documentBack?: File
    selfie: File
  }) => Promise<void>
  userName?: string
  initialStep?: 'document-type' | 'document-front' | 'document-back' | 'selfie' | 'verifying' | 'complete'
  onStepChange?: (step: 'document-type' | 'document-front' | 'document-back' | 'selfie' | 'verifying' | 'complete') => void
  readOnly?: boolean
  selectedDocumentType?: 'drivers_license' | 'passport' | 'state_id' | null
  documentFrontPreview?: string | null
  documentBackPreview?: string | null
  selfiePreview?: string | null
}

export const KYCVerificationFlow: React.FC<KYCVerificationFlowProps> = ({ onComplete, userName, initialStep = 'document-type', onStepChange, readOnly = false, selectedDocumentType, documentFrontPreview: propDocumentFrontPreview, documentBackPreview: propDocumentBackPreview, selfiePreview: propSelfiePreview }) => {
  const [step, setStep] = useState<'document-type' | 'document-front' | 'document-back' | 'selfie' | 'verifying' | 'complete'>(initialStep)
  
  const updateStep = (newStep: typeof step) => {
    setStep(newStep)
    onStepChange?.(newStep)
  }
  const [documentType, setDocumentType] = useState<'drivers_license' | 'passport' | 'state_id'>(selectedDocumentType || 'drivers_license')
  
  // Sync document type from parent when selected through Neela
  React.useEffect(() => {
    if (selectedDocumentType) {
      setDocumentType(selectedDocumentType)
    }
  }, [selectedDocumentType])
  
  const [documentFront, setDocumentFront] = useState<File | null>(null)
  const [documentFrontPreview, setDocumentFrontPreview] = useState<string | null>(propDocumentFrontPreview || null)
  const [documentBack, setDocumentBack] = useState<File | null>(null)
  const [documentBackPreview, setDocumentBackPreview] = useState<string | null>(propDocumentBackPreview || null)
  const [selfie, setSelfie] = useState<File | null>(null)
  const [selfiePreview, setSelfiePreview] = useState<string | null>(propSelfiePreview || null)
  
  // Sync previews from parent when uploaded through Neela
  React.useEffect(() => {
    if (propDocumentFrontPreview) {
      setDocumentFrontPreview(propDocumentFrontPreview)
    }
  }, [propDocumentFrontPreview])
  
  React.useEffect(() => {
    if (propDocumentBackPreview) {
      setDocumentBackPreview(propDocumentBackPreview)
    }
  }, [propDocumentBackPreview])
  
  React.useEffect(() => {
    if (propSelfiePreview) {
      setSelfiePreview(propSelfiePreview)
    }
  }, [propSelfiePreview])
  const [isVerifying, setIsVerifying] = useState(false)
  
  const fileInputFrontRef = useRef<HTMLInputElement>(null)
  const fileInputBackRef = useRef<HTMLInputElement>(null)
  const fileInputSelfieRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (file: File, type: 'front' | 'back' | 'selfie') => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (type === 'front') {
        setDocumentFront(file)
        setDocumentFrontPreview(reader.result as string)
        if (documentType === 'drivers_license' || documentType === 'state_id') {
          updateStep('document-back')
        } else {
          updateStep('selfie')
        }
      } else if (type === 'back') {
        setDocumentBack(file)
        setDocumentBackPreview(reader.result as string)
        updateStep('selfie')
      } else if (type === 'selfie') {
        setSelfie(file)
        setSelfiePreview(reader.result as string)
      }
    }
    reader.readAsDataURL(file)
  }

  const handleStartVerification = async () => {
    if (!documentFront || !selfie) return

    setIsVerifying(true)
    updateStep('verifying')
    
    try {
      await onComplete({
        documentType,
        documentFront,
        documentBack: documentBack || undefined,
        selfie
      })
      updateStep('complete')
    } catch (error) {
      setIsVerifying(false)
      updateStep('selfie')
      throw error
    } finally {
      setIsVerifying(false)
    }
  }

  const getMockIDPreview = () => {
    const mockIDStyle = {
      width: '100%',
      maxWidth: '400px',
      aspectRatio: '1.58 / 1',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '8px',
      padding: '16px',
      color: 'white',
      fontFamily: 'Arial, sans-serif',
      position: 'relative' as const,
      overflow: 'hidden' as const
    }

    return (
      <div style={mockIDStyle} className="shadow-lg">
        <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '8px' }}>UNITED STATES</div>
        <div style={{ fontSize: '8px', opacity: 0.7, marginBottom: '16px' }}>DRIVER LICENSE</div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ width: '80px', height: '80px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
            👤
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              {userName || 'JOHN DOE'}
            </div>
            <div style={{ fontSize: '10px', marginBottom: '2px' }}>DOB: 01/01/1990</div>
            <div style={{ fontSize: '10px', marginBottom: '2px' }}>DL: D1234567</div>
            <div style={{ fontSize: '10px', marginBottom: '2px' }}>EXP: 01/01/2028</div>
            <div style={{ fontSize: '10px', marginTop: '8px' }}>123 MAIN ST, CITY, ST 12345</div>
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: '8px', right: '8px', fontSize: '8px', opacity: 0.6 }}>
          SAMPLE ID
        </div>
      </div>
    )
  }

  const getMockSelfiePreview = () => {
    return (
      <div className="w-full max-w-xs mx-auto">
        <div className="aspect-square rounded-full border-4 border-[#69adba] bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center shadow-lg">
          <div className="text-6xl">📸</div>
        </div>
        <div className="text-center mt-4 text-sm text-slate-600 dark:text-slate-400">
          {userName || 'Your'} Selfie
        </div>
      </div>
    )
  }

  if (step === 'document-type') {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                Identity Verification Required
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                To comply with KYC/AML regulations, we need to verify your identity. This process is secure and takes just a few minutes.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            What type of ID would you like to use?
          </label>
          <div className="grid grid-cols-3 gap-3">
            {(['drivers_license', 'passport', 'state_id'] as const).map((type) => (
              <button
                key={type}
                onClick={() => {
                  setDocumentType(type)
                  updateStep('document-front')
                }}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                  documentType === type
                    ? 'border-[#69adba] bg-[#69adba]/10 dark:bg-[#69adba]/20 text-[#69adba]'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300'
                }`}
              >
                {type === 'drivers_license' ? 'Driver\'s License' : type === 'passport' ? 'Passport' : 'State ID'}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (step === 'document-front') {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Upload Front of {documentType === 'drivers_license' ? 'Driver\'s License' : documentType === 'passport' ? 'Passport' : 'State ID'}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Please take a clear photo of the front of your ID. Make sure all text is readable and there's no glare.
          </p>
        </div>

        {!documentFrontPreview ? (
          <>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 border-2 border-dashed border-slate-300 dark:border-slate-600">
              {getMockIDPreview()}
            </div>
            <input
              ref={fileInputFrontRef}
              type="file"
              accept="image/*"
              capture="environment"
              disabled={readOnly}
              onChange={(e) => {
                if (!readOnly) {
                  const file = e.target.files?.[0]
                  if (file) handleFileSelect(file, 'front')
                }
              }}
              className="hidden"
            />
            <Button
              onClick={() => !readOnly && fileInputFrontRef.current?.click()}
              disabled={readOnly}
              className="w-full bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
            >
              <Camera className="h-4 w-4 mr-2" />
              Take Photo or Upload
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden border-2 border-green-500">
              <img src={documentFrontPreview} alt="ID Front" className="w-full h-auto" />
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                <CheckCircle className="h-4 w-4" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  if (!readOnly) {
                    setDocumentFront(null)
                    setDocumentFrontPreview(null)
                    updateStep('document-type')
                  }
                }}
                disabled={readOnly}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => !readOnly && fileInputFrontRef.current?.click()}
                disabled={readOnly}
                variant="outline"
                className="flex-1"
              >
                Retake
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (step === 'document-back' && (documentType === 'drivers_license' || documentType === 'state_id')) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Upload Back of ID (Optional)
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            If your ID has information on the back, please upload it. Otherwise, you can skip this step.
          </p>
        </div>

        {!documentBackPreview ? (
          <>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 border-2 border-dashed border-slate-300 dark:border-slate-600">
              <div className="text-center text-slate-400 dark:text-slate-500 py-8">
                <Upload className="h-12 w-12 mx-auto mb-2" />
                <p className="text-sm">Back of ID (if applicable)</p>
              </div>
            </div>
            <input
              ref={fileInputBackRef}
              type="file"
              accept="image/*"
              capture="environment"
              disabled={readOnly}
              onChange={(e) => {
                if (!readOnly) {
                  const file = e.target.files?.[0]
                  if (file) handleFileSelect(file, 'back')
                }
              }}
              className="hidden"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => !readOnly && updateStep('selfie')}
                disabled={readOnly}
                className="flex-1"
              >
                Skip
              </Button>
              <Button
                onClick={() => !readOnly && fileInputBackRef.current?.click()}
                disabled={readOnly}
                className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
              >
                <Camera className="h-4 w-4 mr-2" />
                Take Photo
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden border-2 border-green-500">
              <img src={documentBackPreview} alt="ID Back" className="w-full h-auto" />
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                <CheckCircle className="h-4 w-4" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => !readOnly && updateStep('document-front')}
                disabled={readOnly}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  if (!readOnly) {
                    setDocumentBack(null)
                    setDocumentBackPreview(null)
                  }
                }}
                disabled={readOnly}
                variant="outline"
                className="flex-1"
              >
                Remove
              </Button>
              <Button
                onClick={() => !readOnly && updateStep('selfie')}
                disabled={readOnly}
                className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
              >
                Continue
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (step === 'selfie') {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Take a Selfie
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Please take a clear selfie to verify your identity matches your ID. Make sure your face is well-lit and clearly visible.
          </p>
        </div>

        {!selfiePreview ? (
          <>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 border-2 border-dashed border-slate-300 dark:border-slate-600">
              {getMockSelfiePreview()}
            </div>
            <input
              ref={fileInputSelfieRef}
              type="file"
              accept="image/*"
              capture="user"
              disabled={readOnly}
              onChange={(e) => {
                if (!readOnly) {
                  const file = e.target.files?.[0]
                  if (file) handleFileSelect(file, 'selfie')
                }
              }}
              className="hidden"
            />
            <Button
              onClick={() => !readOnly && fileInputSelfieRef.current?.click()}
              disabled={readOnly}
              className="w-full bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
            >
              <Camera className="h-4 w-4 mr-2" />
              Take Selfie
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden border-2 border-green-500 max-w-xs mx-auto">
              <img src={selfiePreview} alt="Selfie" className="w-full h-auto" />
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                <CheckCircle className="h-4 w-4" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  if (!readOnly) {
                    if (documentType === 'drivers_license' || documentType === 'state_id') {
                      setStep('document-back')
                    } else {
                      setStep('document-front')
                    }
                  }
                }}
                disabled={readOnly}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  if (!readOnly) {
                    setSelfie(null)
                    setSelfiePreview(null)
                  }
                }}
                disabled={readOnly}
                variant="outline"
                className="flex-1"
              >
                Retake
              </Button>
              <Button
                onClick={() => !readOnly && handleStartVerification()}
                disabled={isVerifying || readOnly}
                className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
              >
                {isVerifying ? (
                  <>
                    <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Complete Verification'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (step === 'verifying') {
    return (
      <div className="text-center py-8">
        <LoadingSpinner size="lg" className="mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Verifying Your Identity
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          This may take a few moments...
        </p>
      </div>
    )
  }

  if (step === 'complete') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Verification Complete!
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Your identity has been verified. You can now proceed with your 1031 exchange.
        </p>
      </div>
    )
  }

  return null
}

